import { useEffect, useRef, useState } from 'react'
import { Dashboard } from '../components/Dashboard'
import { LaneSearch } from '../components/LaneSearch'
import { RateCard } from '../components/RateCard'
import { RateTicker } from '../components/RateTicker'
import { SponsorSlot } from '../components/SponsorSlot'
import { GATEWAYS, T, useLocale, useT } from '../i18n'
import { CATALOGS } from '../i18n/messages'
import { usePageMeta } from '../lib/meta'
import { useHeroTheme } from '../lib/personal'
import {
  type IndexRow,
  type IndexTrend,
  type LaneRow,
  getIndexLatest,
  getIndexTrend,
  getLanes,
} from '../lib/rates'

export function HomePage() {
  const t = useT()
  const locale = useLocale()
  const gw = GATEWAYS[locale]
  const theme = useHeroTheme()
  usePageMeta(t('meta.home.title'), t('meta.home.desc'), '/')
  const [index, setIndex] = useState<IndexRow[]>([])
  const [lanes, setLanes] = useState<LaneRow[]>([])
  const [trend, setTrend] = useState<Map<number, IndexTrend>>(new Map())
  const [showSticky, setShowSticky] = useState(false)
  const heroSearchRef = useRef<HTMLDivElement>(null)

  // The serif-italic editorial closer only renders for locales that actually define it (EN today).
  const subEm = CATALOGS[locale]?.['home.subEm']

  useEffect(() => {
    getIndexLatest()
      .then(setIndex)
      .catch((e) => console.error('[frm] index latest failed', e))
    getLanes()
      .then(setLanes)
      .catch((e) => console.error('[frm] lanes failed', e))
    getIndexTrend()
      .then(setTrend)
      .catch((e) => console.error('[frm] index trend failed', e))
  }, [])

  useEffect(() => {
    const el = heroSearchRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([e]) => {
        if (e) setShowSticky(!e.isIntersecting && e.boundingClientRect.top < 0)
      },
      { rootMargin: '-72px 0px 0px 0px' }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <div>
      {/* ── Sticky compact search (appears after scrolling past the hero) ──── */}
      <div
        className={`glass fixed inset-x-0 top-16 z-30 border-b border-border transition-all duration-300 ${
          showSticky ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-3 opacity-0'
        }`}
      >
        <div className="mx-auto max-w-3xl px-5 py-2.5">
          <LaneSearch lanes={lanes} variant="compact" />
        </div>
      </div>

      {/* ── Dashboard hero + ticker. Deep-ink island in 'dark', light paper in 'light'. ──── */}
      <div id="rt-hero-dark" className={`overflow-x-clip ${theme === 'dark' ? 'tone-dark' : ''}`}>
        <section className="hero-glow">
          <div className="mx-auto max-w-6xl px-5 pt-9 pb-10">
            {/* Masthead: eyebrow + headline + CTAs (left), justified two-line lede (right) */}
            <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0">
                <span className="eyebrow reveal reveal-1">
                  <span className="live-dot pulse-dot" aria-hidden="true" />
                  {t('home.badge')}
                </span>
                <h1 className="reveal reveal-2 mt-4 text-[clamp(1.9rem,3.6vw,2.75rem)] font-light leading-[1.02] tracking-[-0.04em]">
                  {t('home.h1a')} <span className="hl">{t('home.h1b')}</span>
                  {t('home.h1c')}
                </h1>
                <div className="reveal reveal-3 mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                  <T to="/book" className="font-semibold text-link hover:underline">
                    {t('home.ctaQuote')}
                  </T>
                  <T to="/rate-index" className="text-muted hover:text-ink">
                    {t('home.ctaIndex')}
                  </T>
                  {gw && (
                    <T to={`/${gw.slug}`} className="font-medium text-link hover:underline">
                      {t('country.h1')} →
                    </T>
                  )}
                </div>
              </div>
              <p className="reveal reveal-3 max-w-[23.5rem] shrink-0 text-justify text-[0.95rem] leading-[1.62] text-muted [text-align-last:left] md:pt-1">
                {t('home.subShort')}
                {subEm && <span className="serif ml-1.5 text-[1.08em] italic text-ink">{subEm}</span>}
              </p>
            </div>

            {/* The instrument — full-width search bar */}
            <div ref={heroSearchRef} className="reveal reveal-3 relative z-30 mt-7">
              <LaneSearch lanes={lanes} variant="bar" />
            </div>

            {/* The personal rate terminal */}
            <div className="reveal reveal-4 mt-3.5">
              <Dashboard lanes={lanes} index={index} trend={trend} />
            </div>
          </div>
        </section>

        {/* Thin live ticker band — closes the dark hero, hands off to light content. */}
        <RateTicker lanes={lanes} />
      </div>

      {/* ── Live sailings (light content, SEO depth) ─────────────────────────── */}
      <section className="mx-auto max-w-6xl px-5 pt-16 pb-6">
        <div className="flex items-end justify-between">
          <div>
            <div className="mono text-xs uppercase tracking-[0.18em] text-signal">
              {t('home.liveKicker')}
            </div>
            <h2 className="mt-1.5 text-3xl font-semibold tracking-tight">{t('home.liveTitle')}</h2>
          </div>
          <T to="/rates" className="text-sm text-link hover:underline">
            {t('home.allLanes')}
          </T>
        </div>
        {lanes.length === 0 ? (
          <div className="card mt-6 p-10 text-center text-muted">{t('home.loadingRates')}</div>
        ) : (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {lanes.slice(0, 6).map((l) => (
              <RateCard key={`${l.pol_code}-${l.pod_code}`} lane={l} />
            ))}
          </div>
        )}
        <div className="mt-6">
          <SponsorSlot />
        </div>
      </section>
    </div>
  )
}
